import React from 'react';
import { Button, Divider, Form, List, Popup, Segment } from 'semantic-ui-react';

import { ItemEditable, NombrePersonal } from 'src/componentes';
import { Contexto } from 'src/contexto';
import { DetallesDelCurso, Profesor, Sala } from '../tipos';
import ModalNuevoProfesor from './ModalNuevoProfesor';

type Props = Readonly<{
    curso: DetallesDelCurso;
    salas: readonly Sala[];
    profesores: readonly Profesor[];
    alAsignarSobrecupo: (sobrecupo: number) => Promise<void | boolean>;
    alCambiarSala: (idSala: string) => Promise<void | boolean>;
    alAsignarProfesor: (idProfesor: string) => Promise<void | boolean>;
    alEliminarCurso: () => void;
    alRequerirSalasDeDestino: () => void;
    alRequerirProfesores: () => void;
}>;

type State = Readonly<{
    nuevoSobrecupo: number;
    idNuevaSala: string;
    idNuevoProfesor: string;
    mostrandoCrearProfesor: boolean;
}>;

export class InformacionEditable extends React.Component<Props, State> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public state: State = {
        nuevoSobrecupo: this.props.curso.sobrecupo,
        idNuevaSala: this.props.curso.sala.id,
        idNuevoProfesor: this.props.curso.profesor?.id ?? '',
        mostrandoCrearProfesor: false,
    };

    public render() {
        const {
            curso,
            salas,
            profesores,
            alAsignarSobrecupo,
            alCambiarSala,
            alAsignarProfesor,
            alEliminarCurso,
            alRequerirSalasDeDestino,
            alRequerirProfesores,
        } = this.props;
        const { nuevoSobrecupo, idNuevaSala, idNuevoProfesor, mostrandoCrearProfesor } = this.state;

        const cursoTieneAlumnos = curso.alumnos.length > 0;
        const esAdministradorDeCursos = this.esAdministradorDeCursos();

        const botonEliminar = (
            <Segment basic compact style={{ padding: '0px' }}>
                <Button disabled={cursoTieneAlumnos} content='Eliminar curso' negative />
            </Segment>
        );

        return (
            <>
                <ModalNuevoProfesor
                    modalAbierto={mostrandoCrearProfesor}
                    alRegistrarProfesor={id => {
                        alRequerirProfesores();
                        this.setState({
                            mostrandoCrearProfesor: false,
                            idNuevoProfesor: id,
                        });
                    }}
                    alCerrar={() => this.setState({ mostrandoCrearProfesor: false })}
                />

                <Segment>
                    <List relaxed verticalAlign='middle'>
                        <ItemEditable
                            elementoDeLectura={
                                <Campo etiqueta='Sobrecupos' valor={curso.sobrecupo} />
                            }
                            soloLectura={!esAdministradorDeCursos}
                            elementoEditable={
                                <Form.Input
                                    label='Sobrecupo'
                                    type='number'
                                    value={nuevoSobrecupo}
                                    min={curso.sobrecupoMinimo}
                                    onChange={(_e, { value }) => {
                                        this.setState({ nuevoSobrecupo: parseInt(value, 10) });
                                    }}
                                />
                            }
                            alPresionarGuardar={() => alAsignarSobrecupo(nuevoSobrecupo)}
                            alPresionarCancelar={() => {
                                this.setState({ nuevoSobrecupo: curso.sobrecupo });
                            }}
                        />

                        <ItemEditable
                            elementoDeLectura={<Campo etiqueta='Sala' valor={curso.sala.numero} />}
                            soloLectura={!esAdministradorDeCursos}
                            alPresionarEditar={alRequerirSalasDeDestino}
                            elementoEditable={
                                <Form.Select
                                    loading={salas.length === 0}
                                    label='Sala'
                                    options={salas.map(sala => ({
                                        key: sala.id,
                                        value: sala.id,
                                        text: sala.numero,
                                        content: (
                                            <div>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        minWidth: '5em',
                                                    }}
                                                >
                                                    {sala.numero}
                                                </span>
                                                <span style={{ color: '#555' }}>
                                                    (capacidad: {sala.capacidad})
                                                </span>
                                            </div>
                                        ),
                                        disabled: !sala.esAsignable,
                                    }))}
                                    value={idNuevaSala}
                                    onChange={(_e, { value }) => {
                                        this.setState({ idNuevaSala: value as string });
                                    }}
                                />
                            }
                            alPresionarGuardar={() => alCambiarSala(idNuevaSala)}
                            alPresionarCancelar={() => {
                                this.setState({ idNuevaSala: curso.sala.id });
                            }}
                        />
                        <ItemEditable
                            elementoDeLectura={
                                <Campo
                                    etiqueta='Profesor'
                                    valor={
                                        curso.profesor ? (
                                            <NombrePersonal
                                                nombre={curso.profesor.nombre}
                                                patron='%N %P %M'
                                            />
                                        ) : (
                                            'No asignado'
                                        )
                                    }
                                />
                            }
                            soloLectura={!esAdministradorDeCursos}
                            elementoEditable={
                                <>
                                    <Form.Button
                                        content='Crear nuevo profesor'
                                        type='button'
                                        onClick={() =>
                                            this.setState({ mostrandoCrearProfesor: true })
                                        }
                                    />
                                    <Form.Select
                                        loading={profesores.length === 0}
                                        label='Profesor'
                                        placeholder='Seleccione'
                                        options={profesores.map(profe => ({
                                            key: profe.id,
                                            value: profe.id,
                                            text: <NombrePersonal nombre={profe.nombre} />,
                                        }))}
                                        value={idNuevoProfesor}
                                        onChange={(_e, { value }) => {
                                            this.setState({ idNuevoProfesor: value as string });
                                        }}
                                    />
                                </>
                            }
                            alPresionarEditar={alRequerirProfesores}
                            alPresionarGuardar={() => alAsignarProfesor(idNuevoProfesor)}
                            alPresionarCancelar={() => {
                                this.setState({
                                    idNuevoProfesor: curso.profesor ? curso.profesor.id : '',
                                });
                            }}
                        />
                        <ItemEditable
                            elementoDeLectura={
                                <Campo
                                    etiqueta='Asistentes'
                                    valor={
                                        curso.asistentes.length > 0
                                            ? curso.asistentes.map(asistente => (
                                                  <NombrePersonal
                                                      key={asistente.id}
                                                      nombre={asistente.nombre}
                                                      patron='%N %P %M'
                                                  />
                                              ))
                                            : 'No asignados'
                                    }
                                />
                            }
                        />

                        <div hidden={!esAdministradorDeCursos}>
                            <Divider hidden />

                            {cursoTieneAlumnos ? (
                                <Popup
                                    inverted
                                    trigger={botonEliminar}
                                    hideOnScroll
                                    content='El curso tiene alumnos inscritos'
                                />
                            ) : (
                                <Popup
                                    trigger={botonEliminar}
                                    on='click'
                                    closeOnEscape
                                    hideOnScroll
                                    hoverable
                                    wide
                                >
                                    <Popup.Header content='Esta acción no se puede deshacer' />
                                    <Popup.Content>
                                        <p>¿Está seguro que desea eliminar este curso?</p>

                                        <Button
                                            content='Sí, eliminar'
                                            color='red'
                                            onClick={alEliminarCurso}
                                        />
                                    </Popup.Content>
                                </Popup>
                            )}
                        </div>
                    </List>
                </Segment>
            </>
        );
    }

    private esAdministradorDeCursos() {
        return this.context.usuarioActual.tieneAlgunaDeEstasFunciones('administrador_cursos');
    }
}

const etiquetaStyle: React.CSSProperties = {
    display: 'inline-block',
    minWidth: '7em',
    color: '#454545',
};

const valorStyle: React.CSSProperties = {
    color: '#000',
};

interface CampoProps {
    etiqueta: string;
    valor: React.ReactNode;
}

const Campo: React.FunctionComponent<CampoProps> = props => {
    const { etiqueta, valor } = props;

    return (
        <>
            <span style={etiquetaStyle}>{etiqueta}</span>
            {Array.isArray(valor) ? (
                // tslint:disable-next-line: no-map-without-usage
                valor.map((elem, i) => (
                    <React.Fragment key={i}>
                        <span style={i > 0 ? { ...valorStyle, marginLeft: '7em' } : valorStyle}>
                            {elem}
                        </span>
                        <br />
                    </React.Fragment>
                ))
            ) : (
                <span style={valorStyle}>{valor}</span>
            )}
        </>
    );
};



// WEBPACK FOOTER //
// ./src/modulos/Horarios/DetalleDelCurso/Informacion/InformacionEditable.tsx